import { useRouter } from 'next/router';

import { useSite } from 'src/hooks/useSite';

import { addQueryToRecentSearches } from 'src/utils/recentSearchesFromLocalStorage';

import SearchCombobox from 'components/BaseHelpers/SearchCombobox';
import { localStorageKey } from 'components/Core/CoreGlobalNavigationHeader/components/SearchInput/SearchInput';
import { getTypeaheadResultsFactory } from 'components/Core/CoreGlobalNavigationHeader/components/SearchInput/getTypeaheadResult';

import { siteSearchTypeaheadValueSchema } from '../../utils';

export type SiteSearchComboboxProps = {
  placeholder: string;
  recentSearchesLabel: string;
  className?: string;
  queryDraft: string;
  typeaheadResultsCount: number;
  setQueryDraft: (query: string) => void;
  submitQuery: (query: string) => void;
};

const SiteSearchCombobox = (props: SiteSearchComboboxProps): JSX.Element => {
  const {
    placeholder,
    recentSearchesLabel,
    className,
    queryDraft,
    typeaheadResultsCount,
    setQueryDraft,
    submitQuery,
  } = props;
  const router = useRouter();
  const { site } = useSite();
  const handleSubmit = (query: string) => {
    addQueryToRecentSearches(
      {
        title: query,
        value: JSON.stringify({ title: query, href: '', type: 'query' }),
      },
      localStorageKey
    );
    setQueryDraft(query);
    submitQuery(query);
  };

  return (
    <SearchCombobox
      className={className}
      getTypeaheadResult={getTypeaheadResultsFactory('/api/site-search/typeahead', site)}
      inputValue={queryDraft}
      localStorageKey={localStorageKey}
      onInputValueChange={setQueryDraft}
      onSubmit={handleSubmit}
      placeholder={placeholder}
      recentSearchesLabel={recentSearchesLabel}
      typeaheadResultsCount={typeaheadResultsCount}
      onSelect={(e) => {
        const rawValue = (() => {
          try {
            return JSON.parse(e.itemValue);
          } catch {
            return e.itemValue;
          }
        })();
        const result = siteSearchTypeaheadValueSchema.safeParse(rawValue);

        if (!result.success) {
          router.push(e.itemValue);
          return;
        }

        const { href, title, type } = result.data;
        if (type === 'query') {
          handleSubmit(title);
          return;
        } else if (type === 'link') {
          router.push(href);
        } else {
          const never: never = type;
          return never;
        }
      }}
    />
  );
};

export default SiteSearchCombobox;
